/**
 * GuestCheckinPage — Pagina pubblica /checkin/<token>
 *
 * Multi-step wizard:
 *   1. Welcome (with normative + privacy + booking info)
 *   2. Per-guest form (capofamiglia → familiari) with MRZ scanner
 *   3. Confirmation
 *
 * No authentication. Locale auto-detected from navigator + manual switcher.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, IdentificationCard, Camera, ArrowRight, ArrowLeft, CheckCircle, Warning, House, CalendarBlank, Users, Globe,
} from '@phosphor-icons/react';
import { t as tr, detectLocale, SUPPORTED_LOCALES, LOCALE_LABELS, LOCALE_FLAGS } from '../lib/checkinI18n';
import MRZScanner from '../components/MRZScanner';
import LookupCombobox from '../components/LookupCombobox';
import { iso3ToIstat, icaoToDocType } from '../lib/mrzParser';
import { dateIT } from '../lib/dateIT';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


function emptyGuest() {
  return {
    cognome: '',
    nome: '',
    sesso: 'M',
    birth_date: '',
    birth_state_code: '100000100',  // Italy by default
    birth_place_code: '',
    birth_province: '',
    citizenship_code: '100000100',
    document_type: 'identita',
    document_number: '',
    document_place_code: '',
    mrz_doc_expiry: '',
  };
}


export default function GuestCheckinPage() {
  const { token } = useParams();
  const [locale, setLocale] = useState(detectLocale());
  const [phase, setPhase] = useState('loading'); // loading | welcome | form | done | error
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [guestIndex, setGuestIndex] = useState(0);
  const [guest, setGuest] = useState(emptyGuest());
  const [submitting, setSubmitting] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const t = useCallback((k, vars) => tr(locale, k, vars), [locale]);

  const fetchInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/checkin/${token}`);
      setInfo(res.data);
      if (res.data.is_sent || res.data.all_done) {
        setPhase('done');
      } else {
        setPhase('welcome');
        setGuestIndex(res.data.guests_completed || 0);
      }
    } catch (err) {
      const status = err.response?.status;
      setErrorMsg(status === 410 ? t('error_expired') : t('error_invalid'));
      setPhase('error');
    }
  }, [token, t]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const isCapofamiglia = guestIndex === 0 && info?.guests_count > 1;
  const isSingolo = info?.guests_count === 1;
  const isLast = info && guestIndex === info.guests_count - 1;

  const onMRZParsed = (parsed) => {
    // Auto-fill from MRZ. Privacy: parsed.raw is kept ONLY in state, NOT logged.
    const sex = parsed.sesso || 'M';
    const nationalityIstat = iso3ToIstat(parsed.nationality_iso3);
    setGuest(g => ({
      ...g,
      cognome: parsed.cognome || g.cognome,
      nome: parsed.nome || g.nome,
      sesso: sex === 'M' || sex === 'F' ? sex : g.sesso,
      birth_date: parsed.birth_date || g.birth_date,
      // The MRZ contains nationality but not birth-state directly. Best-effort:
      // assume birth state == nationality (common case). User can override.
      birth_state_code: nationalityIstat || g.birth_state_code,
      citizenship_code: nationalityIstat || g.citizenship_code,
      document_type: icaoToDocType(parsed.icao_doc_code) || g.document_type,
      document_number: parsed.document_number || g.document_number,
      document_place_code: iso3ToIstat(parsed.issuing_country_iso3) || g.document_place_code,
      mrz_doc_expiry: parsed.document_expiry || '',
    }));
    setScannerOpen(false);
  };

  const submitGuest = async () => {
    if (!guest.cognome.trim() || !guest.nome.trim() || !guest.birth_date) {
      setErrorMsg(t('required'));
      return;
    }
    setErrorMsg('');
    setSubmitting(true);
    try {
      const tipo = isSingolo ? 'singolo' : (isCapofamiglia ? 'capofamiglia' : 'familiare');
      const payload = { ...guest, tipo_alloggiato: tipo };
      const res = await axios.post(`${API}/public/checkin/${token}/guest`, payload);
      if (res.data.all_done) {
        setPhase('done');
      } else {
        setGuestIndex(res.data.position);
        setGuest(emptyGuest());
        // Scroll to top for next guest
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || t('error_invalid'));
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────── Render ───────────

  if (phase === 'loading') {
    return <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center text-sm overflow-x-hidden">…</div>;
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-4 overflow-x-hidden" data-testid="checkin-error">
        <div className="max-w-md text-center">
          <Warning size={48} weight="duotone" className="text-amber-400 mx-auto mb-3" />
          <p className="text-lg font-black mb-1 break-words">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-700 to-emerald-900 text-white flex items-center justify-center px-4 overflow-x-hidden" data-testid="checkin-done">
        <div className="max-w-md text-center">
          <CheckCircle size={64} weight="fill" className="mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-2 break-words">{t('success_title')}</h1>
          <p className="text-sm text-white/85 mb-3 leading-relaxed break-words">{t('success_body')}</p>
          <p className="text-[11px] text-white/70 leading-relaxed break-words">{t('success_purge')}</p>
        </div>
      </div>
    );
  }

  // ─────────── Welcome ───────────
  if (phase === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 overflow-x-hidden relative" data-testid="checkin-welcome">
        <LocaleSwitcher locale={locale} setLocale={setLocale} />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-900 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider mb-4">
            <ShieldCheck size={11} weight="bold" /> {t('welcome_badge')}
          </div>

          <h1 className="text-2xl sm:text-4xl font-black tracking-tight mb-3 text-zinc-900 break-words">
            {t('welcome_title', { property: info.property_name })}
          </h1>
          <p className="text-sm text-zinc-600 leading-relaxed mb-6 break-words">{t('welcome_subtitle')}</p>

          <div className="bg-white border border-zinc-200 p-4 sm:p-5 mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <InfoStat icon={CalendarBlank} label={t('booking_dates', { check_in: info.check_in, check_out: info.check_out, nights: info.nights, nights_plural: info.nights === 1 ? '' : 'i' })} />
            <InfoStat icon={Users} label={t('booking_guests', { guests_count: info.guests_count, guests_plural: info.guests_count === 1 ? 'e' : 'i' })} />
            <InfoStat icon={House} label={info.property_name} />
          </div>

          <div className="bg-zinc-900 text-white p-4 sm:p-5 mb-4">
            <p className="text-[10px] font-black uppercase tracking-wider text-violet-300 mb-1.5">{t('legal_title')}</p>
            <p className="text-xs text-zinc-300 leading-relaxed">{t('legal_body')}</p>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 p-3 sm:p-4 flex items-start gap-2.5 mb-6">
            <ShieldCheck size={16} weight="bold" className="text-emerald-700 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-emerald-900 leading-relaxed">{t('privacy_no_image')}</p>
          </div>

          <button
            onClick={() => setPhase('form')}
            className="w-full bg-violet-700 hover:bg-violet-800 text-white font-black uppercase tracking-wider text-sm py-4 transition-colors flex items-center justify-center gap-2"
            data-testid="checkin-start"
          >
            {t('start_btn')} <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  // ─────────── Form ───────────
  return (
    <div className="min-h-screen bg-zinc-50 overflow-x-hidden relative" data-testid="checkin-form">
      <LocaleSwitcher locale={locale} setLocale={setLocale} />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-violet-700 text-white">
            {t('guest_step', { n: guestIndex + 1, total: info.guests_count })}
          </span>
          <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500">
            {isSingolo ? t('singolo_label') : (isCapofamiglia ? t('capofamiglia_label') : t('familiare_label'))}
          </span>
        </div>
        <h1 className="text-2xl font-black tracking-tight text-zinc-900 mb-1 break-words">{guest.nome || t('field_nome')} {guest.cognome || ''}</h1>
        <p className="text-xs text-zinc-500 mb-5 break-words">{info.property_name} · {dateIT(info.check_in)} → {dateIT(info.check_out)}</p>

        {/* MRZ scanner trigger */}
        <div className="bg-gradient-to-br from-violet-700 to-fuchsia-700 text-white p-4 sm:p-5 mb-5">
          <div className="flex items-start gap-3">
            <IdentificationCard size={22} weight="bold" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black tracking-tight">{t('scan_doc_title')}</p>
              <p className="text-xs text-white/80 leading-snug mt-0.5">{t('scan_doc_subtitle')}</p>
            </div>
          </div>
          <button
            onClick={() => setScannerOpen(true)}
            className="mt-3 w-full bg-white text-violet-800 font-black uppercase tracking-wider text-sm py-3 flex items-center justify-center gap-2 hover:bg-violet-50 transition-colors"
            data-testid="checkin-scan-btn"
          >
            <Camera size={16} weight="bold" /> {t('scan_start')}
          </button>
          <p className="text-[10px] text-white/70 text-center mt-1.5">{t('skip_scan')}</p>
        </div>

        {/* Form fields */}
        <div className="bg-white border border-zinc-200 p-4 sm:p-5 space-y-3 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t('field_cognome') + ' *'}>
              <input type="text" value={guest.cognome} onChange={e => setGuest(g => ({ ...g, cognome: e.target.value.toUpperCase() }))} className="form-input" required data-testid="ck-cognome" />
            </Field>
            <Field label={t('field_nome') + ' *'}>
              <input type="text" value={guest.nome} onChange={e => setGuest(g => ({ ...g, nome: e.target.value.toUpperCase() }))} className="form-input" required data-testid="ck-nome" />
            </Field>
            <Field label={t('field_sesso') + ' *'}>
              <select value={guest.sesso} onChange={e => setGuest(g => ({ ...g, sesso: e.target.value }))} className="form-input" data-testid="ck-sesso">
                <option value="M">{t('field_male')}</option>
                <option value="F">{t('field_female')}</option>
              </select>
            </Field>
            <Field label={t('field_birth_date') + ' *'}>
              <input type="date" value={guest.birth_date} onChange={e => setGuest(g => ({ ...g, birth_date: e.target.value }))} className="form-input" required data-testid="ck-birth-date" />
            </Field>
            <Field label={t('field_birth_state') + ' *'} help={t('field_birth_state_help')}>
              <LookupCombobox
                endpoint="/public/checkin/lookup/stati"
                value={guest.birth_state_code}
                onChange={(it) => setGuest(g => ({ ...g, birth_state_code: it.code }))}
                placeholder="Es. ITALIA, GERMANIA..."
                required
                testid="ck-birth-state"
              />
            </Field>
            {guest.birth_state_code === '100000100' && (
              <>
                <Field label={t('field_birth_place') + ' *'} help={t('field_birth_place_help')}>
                  <LookupCombobox
                    endpoint="/public/checkin/lookup/comuni"
                    value={guest.birth_place_code}
                    onChange={(it) => setGuest(g => ({ ...g, birth_place_code: it.code, birth_province: it.province || g.birth_province }))}
                    placeholder="Es. ROMA, MILANO, NAPOLI..."
                    required
                    testid="ck-birth-place"
                  />
                </Field>
                <Field label={t('field_birth_province') + ' *'} help={t('field_birth_province_help')}>
                  <input type="text" value={guest.birth_province} onChange={e => setGuest(g => ({ ...g, birth_province: e.target.value.toUpperCase().slice(0,2) }))} className="form-input tabular-nums uppercase" maxLength="2" required data-testid="ck-birth-province" placeholder="RM" />
                </Field>
              </>
            )}
            <Field label={t('field_citizenship') + ' *'} help={t('field_citizenship_help')}>
              <LookupCombobox
                endpoint="/public/checkin/lookup/stati"
                value={guest.citizenship_code}
                onChange={(it) => setGuest(g => ({ ...g, citizenship_code: it.code }))}
                placeholder="Es. ITALIA, FRANCIA..."
                required
                testid="ck-citizenship"
              />
            </Field>
            <Field label={t('field_doc_type') + ' *'}>
              <select value={guest.document_type} onChange={e => setGuest(g => ({ ...g, document_type: e.target.value }))} className="form-input" data-testid="ck-doc-type">
                <option value="identita">{t('doc_identita')}</option>
                <option value="cart_ident_el">{t('doc_cart_ident_el')}</option>
                <option value="passaporto">{t('doc_passaporto')}</option>
                <option value="patente">{t('doc_patente')}</option>
                <option value="perm_soggiorno">{t('doc_perm_soggiorno')}</option>
              </select>
            </Field>
            <Field label={t('field_doc_number') + ' *'}>
              <input type="text" value={guest.document_number} onChange={e => setGuest(g => ({ ...g, document_number: e.target.value.toUpperCase() }))} className="form-input tabular-nums" maxLength="20" required data-testid="ck-doc-number" />
            </Field>
            <Field label={t('field_doc_place') + ' *'}>
              <LookupCombobox
                endpoint={guest.birth_state_code === '100000100' ? "/public/checkin/lookup/comuni" : "/public/checkin/lookup/stati"}
                value={guest.document_place_code}
                onChange={(it) => setGuest(g => ({ ...g, document_place_code: it.code }))}
                placeholder="Comune o Stato di rilascio..."
                required
                testid="ck-doc-place"
              />
            </Field>
          </div>
        </div>

        {errorMsg && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-xs p-3 mb-4 flex items-center gap-2">
            <Warning size={14} weight="bold" /> {errorMsg}
          </div>
        )}

        <button
          onClick={submitGuest}
          disabled={submitting}
          className="w-full bg-violet-700 hover:bg-violet-800 text-white font-black uppercase tracking-wider text-sm py-4 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          data-testid="ck-submit"
        >
          {submitting ? t('submitting') : (
            <>
              {isLast ? t('last_submit_btn') : t('submit_btn')}
              <ArrowRight size={16} weight="bold" />
            </>
          )}
        </button>

        {guestIndex > 0 && (
          <button
            onClick={() => { setGuestIndex(i => Math.max(0, i - 1)); setGuest(emptyGuest()); }}
            className="mt-2 w-full text-zinc-500 hover:text-zinc-900 text-xs font-bold uppercase tracking-wider py-2 flex items-center justify-center gap-1"
          >
            <ArrowLeft size={11} weight="bold" /> {t('field_nome')} {guestIndex}
          </button>
        )}

        <style>{`.form-input{width:100%;height:40px;padding:0 12px;border:1px solid #d4d4d8;font-size:14px}.form-input:focus{outline:none;border-color:#7c3aed}`}</style>
      </div>

      <MRZScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onParsed={onMRZParsed} t={t} locale={locale} />
    </div>
  );
}


function InfoStat({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} weight="bold" className="text-violet-700 flex-shrink-0" />
      <p className="text-xs text-zinc-700 leading-tight">{label}</p>
    </div>
  );
}


function Field({ label, help, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      {children}
      {help && <p className="text-[10px] text-zinc-400 mt-1 leading-tight">{help}</p>}
    </div>
  );
}


function LocaleSwitcher({ locale, setLocale }) {
  return (
    <div className="absolute top-3 right-3 z-10 max-w-[40%]" data-testid="locale-switcher">
      <select
        value={locale}
        onChange={(e) => setLocale(e.target.value)}
        className="bg-white border border-zinc-300 text-xs font-bold px-2 py-1.5 cursor-pointer max-w-full truncate"
        data-testid="locale-select"
      >
        {SUPPORTED_LOCALES.map(l => (
          <option key={l} value={l}>{LOCALE_FLAGS[l]} {LOCALE_LABELS[l]}</option>
        ))}
      </select>
    </div>
  );
}
